import { supabase } from '../lib/supabase';
import type {
  CampaignInfluencer,
  CampaignInfluencerWithStats,
  CampaignInfluencerStatus,
  CommissionType,
} from '../types/multiInfluencer';

export const campaignInfluencerService = {
  /**
   * Get all influencers for a campaign
   */
  async getByCampaign(campaignId: string): Promise<CampaignInfluencerWithStats[]> {
    const { data: influencers, error } = await supabase
      .from('campaign_influencers')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) throw error;

    // Enrich with stats
    const enriched = await Promise.all(
      (influencers || []).map(async (inf) => {
        const stats = await this.getInfluencerStats(inf.id);
        return {
          ...inf,
          ...stats,
        };
      })
    );

    return enriched;
  },

  /**
   * Get stats for a campaign influencer
   */
  async getInfluencerStats(campaignInfluencerId: string) {
    // Get deliverable counts
    const { data: deliverables } = await supabase
      .from('deliverables')
      .select('status')
      .eq('campaign_influencer_id', campaignInfluencerId);

    const deliverable_count = deliverables?.length || 0;
    const published_count =
      deliverables?.filter((d: any) => d.status === 'Published').length || 0;

    // Get invoice data
    const { data: invoices } = await supabase
      .from('invoices')
      .select('id, total, status')
      .eq('campaign_influencer_id', campaignInfluencerId)
      .eq('type', 'Influencer');

    const invoice_count = invoices?.length || 0;

    // Get payment totals
    let total_paid = 0;
    if (invoices && invoices.length > 0) {
      const invoiceIds = invoices.map((inv: any) => inv.id);
      const { data: payments } = await supabase
        .from('payments')
        .select('amount')
        .in('invoice_id', invoiceIds);

      total_paid = payments?.reduce((sum: number, p: any) => sum + p.amount, 0) || 0;
    }

    // Get influencer details
    const { data: influencer } = await supabase
      .from('campaign_influencers')
      .select('agreed_fee, commission_pct, commission_flat_fee, commission_type')
      .eq('id', campaignInfluencerId)
      .maybeSingle();

    const agreed_fee = influencer?.agreed_fee || 0;
    const commission_pct = influencer?.commission_pct || 0.3;
    const commission_flat_fee = influencer?.commission_flat_fee || 0;
    const commission_type: CommissionType = influencer?.commission_type || 'percentage';
    const commission_amount = commission_type === 'flat_fee'
      ? commission_flat_fee
      : agreed_fee * commission_pct;
    const net_fee_influencer = agreed_fee - commission_amount;

    return {
      deliverable_count,
      published_count,
      invoice_count,
      total_paid,
      net_fee_influencer,
      commission_amount,
    };
  },

  /**
   * Create a new campaign influencer
   */
  async create(data: Omit<CampaignInfluencer, 'id' | 'created_at' | 'updated_at'>) {
    const { data: created, error } = await supabase
      .from('campaign_influencers')
      .insert(data)
      .select()
      .maybeSingle();

    if (error) throw error;
    if (!created) throw new Error('Failed to create campaign influencer');
    return created;
  },

  /**
   * Update campaign influencer
   */
  async update(id: string, data: Partial<CampaignInfluencer>) {
    const { data: updated, error } = await supabase
      .from('campaign_influencers')
      .update(data)
      .eq('id', id)
      .select()
      .maybeSingle();

    if (error) throw error;
    if (!updated) throw new Error('Campaign influencer not found or update failed');
    return updated;
  },

  /**
   * Update influencer status
   */
  async updateStatus(id: string, status: CampaignInfluencerStatus) {
    return this.update(id, { status });
  },

  /**
   * Delete campaign influencer
   */
  async delete(id: string) {
    const { error } = await supabase
      .from('campaign_influencers')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Get single campaign influencer by ID
   */
  async getById(id: string): Promise<CampaignInfluencer | null> {
    const { data, error } = await supabase
      .from('campaign_influencers')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Check if influencer name exists in campaign
   */
  async existsInCampaign(campaignId: string, influencerName: string): Promise<boolean> {
    const { data } = await supabase
      .from('campaign_influencers')
      .select('id')
      .eq('campaign_id', campaignId)
      .ilike('influencer_name', influencerName)
      .maybeSingle();

    return !!data;
  },
};
